import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FILTER_TYPES, LOGIC_TYPE } from "../../utils/constants";
import { getRoles } from "../../utils/OktaHelpers";
import {
    getReactSelectStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import { isAuthorized } from "../../utils/RolesHelpers";
import CompareFilter from "../FilterChildren/CompareFilter";
import VerticalRadioButtons from "../RadioButtons/VerticalRadioButtons";

import ValidationError from "../Shared/ValidationError";

const HorizontalForm = ({
    fields,
    input,
    setInput,
    isFormSubmitted,
    error = {},
    gridCols = "3",
}) => {
    const { t } = useTranslation();
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        setRoles(getRoles());
    }, []);

    const getErrorMessage = (field) => {
        return error[isFormSubmitted && !field.disabled && field.name];
    };

    const handleMultiSelectChange = (selection, action) => {
        handleInputChangeByValue(action.name, selection);
    };

    const handleSelectChange = (selection, action) => {
        setInput((prevInput) => ({
            ...prevInput,
            [action.name]: action.action === "clear" ? null : selection,
        }));
    };

    const handleInputChangeByEvent = (e) => {
        setInput({
            ...input,
            [e.target.name]: e.target.value,
        });
    };

    const handleInputChangeByValue = (key, value) => {
        setInput({
            ...input,
            [key]: value,
        });
    };

    const handleCompareType = (e) => {
        setInput((prevInput) => ({
            ...prevInput,
            [e.target.name]: {
                ...prevInput[e.target.name],
                type: e.target.value,
            },
        }));
    };

    const handleRadioChange = (e) => {
        setInput((prevInput) => ({
            ...prevInput,
            [e.target.name]: e.target.value,
        }));
    };

    const getSelectInputType = (field) => {
        const getSelectHandler = () => {
            if (field.handler) return field.handler;

            return field.isMulti ? handleMultiSelectChange : handleSelectChange;
        };

        return (
            <Select
                isClearable={field.isClearable}
                isMulti={field.isMulti}
                isSearchable
                name={field.name}
                onChange={getSelectHandler()}
                options={field.options}
                getOptionValue={(option) => option.searchable || option.value}
                value={input[field.name] || ""}
                styles={getReactSelectStyling(
                    {
                        showError: getErrorMessage(field),
                        isMultiSelect: field.isMulti,
                    }
                    // "auto"
                )}
                isOptionDisabled={field.optionsDisableCallback}
                theme={getReactSelectTheme}
                isDisabled={field.disabled}
                placeholder={field.placeholder || ""}
                noOptionsMessage={() => field.emptyMessage}
                isLoading={field.isLoading}
            />
        );
    };

    const getRadioButtons = (field) => {
        const getSelectHandler = () => {
            if (field.handler) return field.handler;

            return handleRadioChange;
        };
        return (
            <VerticalRadioButtons
                name={field.name}
                options={field.options}
                onChangeHandler={getSelectHandler()}
                checkedReference={input[field.name]}
            />
        );
    };

    const getDefaultInputType = (field) => {
        return (
            <input
                type={field.type || "text"}
                name={field.name}
                value={input[field.name] || ""}
                onChange={handleInputChangeByEvent}
                placeholder={field.placeholder}
                min={field?.min || ""}
                max={field?.max || ""}
                className={`input-element ${field.disabled ? "disabled" : ""} ${
                    field.className || ""
                }`}
                disabled={field.disabled}
            />
        );
    };

    const handleCompareChange = (name, compare, index) => {
        setInput((prevInput) => {
            const compareTarget = input[name];
            compareTarget.filters[index] = compare || {
                target: "",
                compare: "",
                value: "",
                unit: "",
            };
            return {
                ...prevInput,
                [name]: compareTarget,
            };
        });
    };

    const getCompareInputType = (field) => {
        return (
            <div className="flex flex-col gap-2 border p-2 rounded-sm">
                {input[field.name]?.filters?.map((filter, index) => {
                    return (
                        <CompareFilter
                            key={index}
                            field={field}
                            input={filter}
                            handleTargetChange={(event) =>
                                handleCompareChange(
                                    field.name,
                                    event
                                        ? {
                                              ...filter,
                                              target: event.value,
                                              unit: event.unit,
                                          }
                                        : null,
                                    index
                                )
                            }
                            handleCompareChange={(event) =>
                                handleCompareChange(
                                    field.name,
                                    { ...filter, compare: event?.value ?? "" },
                                    index
                                )
                            }
                            handleValueChange={(event) =>
                                handleCompareChange(
                                    event.target.name,
                                    {
                                        ...filter,
                                        value: event.target.value,
                                    },
                                    index
                                )
                            }
                        />
                    );
                })}
            </div>
        );
    };

    const renderInputType = (field) => {
        switch (field.type) {
            case FILTER_TYPES.select:
                return getSelectInputType(field);
            case FILTER_TYPES.compare:
                return getCompareInputType(field);
            case FILTER_TYPES.radio:
                return getRadioButtons(field);
            case FILTER_TYPES.text:
            default:
                return getDefaultInputType(field);
        }
    };

    const compareTypeOptions = [
        {
            name: LOGIC_TYPE.or,
            value: LOGIC_TYPE.or,
            label: t(LOGIC_TYPE.or),
        },
        {
            name: LOGIC_TYPE.and,
            value: LOGIC_TYPE.and,
            label: t(LOGIC_TYPE.and),
        },
    ];

    const renderInputOption = (field, index) => {
        return (
            <div
                key={index}
                className={`input-group ${
                    getErrorMessage(field) ? "invalid-warning-input" : ""
                }`}
            >
                <div className="flex items-center justify-between">
                    <label
                        htmlFor={`dynamic-input-${index + 1}`}
                        className={`uppercase ${
                            field.required ? "required" : ""
                        }`}
                    >
                        {field.label}
                    </label>
                    {field.type === FILTER_TYPES.compare && (
                        <div className="flex mb-2">
                            <VerticalRadioButtons
                                name={field.name}
                                options={compareTypeOptions}
                                onChangeHandler={handleCompareType}
                                checkedReference={
                                    input[field.name]?.type || LOGIC_TYPE.and
                                }
                                wrapperClassName="bg-white w-full h-2/3 rounded text-primary-color-blue my-0"
                                labelClassName="uppercase"
                            />
                        </div>
                    )}
                </div>
                {renderInputType(field)}
                <ValidationError errorMessage={getErrorMessage(field)} />
            </div>
        );
    };

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                if (!field.roles || isAuthorized(field.roles, roles)) {
                    return renderInputOption(field, index);
                }
            })
        );
    };

    return (
        <>
            <div
                style={{
                    gridGap: "10px",
                }}
                className={`main-customer-form-inputs w-full grid-${gridCols}-cols pb-5`}
            >
                {renderFields()}
            </div>
        </>
    );
};

export default HorizontalForm;
